import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  inject,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { Subject, Subscription, takeUntil, filter, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  CommentAnnotationPlugin,
  PluginConfig,
  Environment,
} from '@mtm/conversation-client-sdk-annotation';
import { onConversationPluginReady, onPluginReady, onReplyCommentAnnotation } from '../../services/annotation.service';
import { MtmStompService, wsListenerANNOTATION_CREATED } from '../../services/mtm-stomp.service';
import { AuthService } from '../../services/auth.service';
import { EmitterService } from '../../services/emitter.service';
import {
  AiOptions,
  aiParamsFromAnnotationEvent,
  AnnotationSendOtherTypesMessage,
  AnnotationDeleteMessage,
  AnnotationEditMessage,
  AnnotationSelectThreadNew,
} from '../../interfaces';
import { PlatformCommentHandler } from './platform-comment-handler';
import { HttpClient } from '@angular/common/http';
import {
  PlatformRbacSignatureService,
  SignatureRequestResult,
} from '../../services/platform-rbac-signature.service';
import { TopMediaInfoService } from '../top-media-info/top-media-info.service';
import { commentFileAdd } from '../../services/comments.service';
import { AppStateService } from '../../services/app-state.service';
import { environment } from '../../../../environments/environment';
import { onLoadProject, ProjectService } from '../../services/project.service';
import { Router } from '@angular/router';
import { NavigationRequest } from '@mtm/conversation-ui-angular';
import { PlatformCommentsChatStateService } from '../../services/platform-comments-chat-state.service';
import { RightPanelCoordinatorService } from '../../services/right-panel-coordinator.service';

@Component({
  selector: 'mtm-platform-comments-chat',
  templateUrl: './platform-comments-chat.component.html',
  styleUrl: './platform-comments-chat.component.scss',
  standalone: false,
})
export class PlatformCommentsChatComponent implements OnInit, OnDestroy, OnChanges {
  @Input() entityType: string;
  @Input() pageId: string;
  @Input() entityName: string;
  @Input() projectId?: string | null;
  @Input() participants: any[] = [];
  @Input() viewMode: 'toggle' | 'split' = 'toggle';
  @Output() isLoadingChange = new EventEmitter<boolean>();
  @ViewChild('platformCommentsChat', { static: true }) platformCommentsChatRef!: ElementRef;
  @ViewChild('conversationInterface', { static: false }) conversationInterfaceRef!: ElementRef;

  instanceId: string = Math.random().toString(36).substring(2, 15);
  isMediumScreen: boolean = false;
  calculatedHeight: number = 0;

  commentAnnotationPlugin: CommentAnnotationPlugin;
  pluginReady: boolean = false;
  platformCommentHandler: PlatformCommentHandler;
  annotationComment: string = '';
  newFiles: any[] = [];
  currentUsername: string = '';

  signature: SignatureRequestResult | null = null;
  private signatureRequestSub: Subscription | null = null;

  get resolvedEntityName(): string {
    return this.entityName || this.signature?.itemName || '';
  }
  wsId: string;
  navigationRequest: NavigationRequest | null = null;
  private pendingNavRequest: NavigationRequest | null = null;
  isChannelManagerVisible: boolean = false;
  ngUnsubscribe = new Subject<void>();
  isBusy: boolean = false;
  projectName: string = '';
  versionName: string = '';

  private readonly authService = inject(AuthService);
  private readonly httpClient = inject(HttpClient);
  private readonly topMediaInfoService = inject(TopMediaInfoService);
  private readonly stompService = inject(MtmStompService);
  private readonly appState = inject(AppStateService);
  private readonly projectService = inject(ProjectService);
  private readonly platformChatState = inject(PlatformCommentsChatStateService);

  private readonly router = inject(Router);
  private readonly panelCoordinator = inject(RightPanelCoordinatorService);
  private readonly rbacSignature = inject(PlatformRbacSignatureService);
  private hasPerformedFullInit = false;

  constructor() {}

  ngOnInit(): void {
    this.currentUsername = this.authService.getAuthUserName();

    onLoadProject.pipe(takeUntil(this.ngUnsubscribe)).subscribe((project) => {
      this.projectName = project?.name || '';
    });

    this.platformChatState.versionData$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((versionData) => {
      this.versionName = versionData?.versionName || '';
    });


    this.panelCoordinator.activePanel$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((panel) => panel === 'comment')
      )
      .subscribe(() => this.handlePanelActivation());

    if (this.panelCoordinator.isActive('comment')) {
      this.handlePanelActivation();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.panelCoordinator.isActive('comment')) return;

    if (!this.hasPerformedFullInit && this.pageId && this.entityType) {
      this.performFullInit();
      return;
    }

    const pageIdChanged =
      !!changes['pageId'] &&
      !changes['pageId'].firstChange &&
      changes['pageId'].currentValue !== changes['pageId'].previousValue;
    const entityTypeChanged =
      !!changes['entityType'] &&
      !changes['entityType'].firstChange &&
      changes['entityType'].currentValue !== changes['entityType'].previousValue;
    const entityNameChanged =
      !!changes['entityName'] &&
      !changes['entityName'].firstChange &&
      changes['entityName'].currentValue !== changes['entityName'].previousValue;
    const participantsChanged = !!changes['participants'] && !changes['participants'].firstChange;

    if (pageIdChanged || entityTypeChanged || entityNameChanged || participantsChanged) {
      this.reinitializeForInputsChange();
    }
  }

  private handlePanelActivation(): void {
    if (!this.pageId || !this.entityType) return;

    if (!this.hasPerformedFullInit) {
      this.performFullInit();
      return;
    }

    if (this.panelCoordinator.needsReinitialization('comment', this.pageId, this.entityType)) {
      this.reinitializeForInputsChange();
    }
  }

  private performFullInit(): void {
    this.hasPerformedFullInit = true;
    this.registerEventListeners();
    this.fetchSignatureAndInit();

    this.platformChatState.navigationRequest$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((request) => {
      this.applyOrDeferNavRequest(request);
    });

    onReplyCommentAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onReplyCommentAnnotation(args),
    });
  }

  private reinitializeForInputsChange(): void {
    if (this.platformCommentHandler && this.pluginReady) {
      this.platformCommentHandler.teardown();
    }
    this.platformCommentHandler = null as any;
    this.instanceId = Math.random().toString(36).substring(2, 15);
    this.pluginReady = false;
    this.pendingNavRequest = null;
    this.commentAnnotationPlugin = null as any;
    this.appState.setCommentPluginReady(false);

    if (this.wsId) {
      this.stompService.unsubscribeToService(this.wsId);
    }

    this.fetchSignatureAndInit();
  }

  wsOnAnnotationCreate(args: any) {
    commentFileAdd.emit({
      projectId: this.pageId,
      sectionId: this.pageId,
      subSectionId: this.pageId,
      comment: args.mediaAnnotation.comments[0],
      annotationId: args.mediaAnnotation.id,
      files: this.newFiles,
    });
    this.cleanUpFiles();
  }

  cleanUpFiles() {
    this.newFiles = [];
  }

  onReplyCommentAnnotation(args: any) {
    if (args.itemId != this.pageId || args.instanceId != this.instanceId) {
      return;
    }
    if (this.instanceId != args.instanceId) {
      return;
    }
    if (
      args.data.annotation &&
      this.platformCommentHandler &&
      args.data.commentBody &&
      this.pluginReady &&
      this.pageId == this.commentAnnotationPlugin.getItem().id
    ) {
      this.platformCommentHandler.replyToComment(args.data.annotation.id, args.data.commentBody, args.data.files);
    }
  }

  private initPlugin(): void {
    const userInfo: any = this.authService.getAuthUser();
    const config: PluginConfig = {
      userInfo,
      item: { id: this.pageId },
      entityName: this.entityName || this.signature?.itemName || this.entityType,
      annotationObjects: [],
      instanceId: this.instanceId,
      itemSignature: this.signature?.signature?.access_token,
      httpClient: this.httpClient,
      participants: this.prepareParticipantList(),
      environment: environment.environmentType as Environment,
    };

    this.pluginReady = false;
    this.appState.setCommentPluginReady(false);
    this.isLoadingChange.emit(true);
    this.commentAnnotationPlugin = new CommentAnnotationPlugin(config);
    this.commentAnnotationPlugin.onReady?.(() => {
      this.pluginReady = true;
      this.appState.setCommentPluginReady(true);
      this.isLoadingChange.emit(false);
      onConversationPluginReady.emit({
        annotationPlugin: this.commentAnnotationPlugin,
        instanceId: this.instanceId,
        itemId: this.pageId,
      });
      this.platformCommentHandler = new PlatformCommentHandler(
        this.commentAnnotationPlugin,
        { id: this.pageId },
        this.instanceId
      );

      onPluginReady.emit({
        type: 'comment',
        annotationPlugin: this.commentAnnotationPlugin,
        instanceId: this.instanceId,
        itemId: this.pageId,
      });

      const pendingRequest = this.platformChatState.getPendingNavigationRequest() as any;
      if (pendingRequest && (!pendingRequest.targetId || pendingRequest.targetId === this.pageId)) {
        this.navigationRequest = this.platformChatState.consumePendingNavigationRequest();
        this.pendingNavRequest = null;
      } else if (this.pendingNavRequest) {
        this.navigationRequest = this.pendingNavRequest;
        this.pendingNavRequest = null;
      }
    });
  }

  private prepareParticipantList() {
    const authUser = this.authService.getAuthUser();
    const inputParticipants = this.participants || [];

    const normalizedAuthEmail = (authUser?.email || '').toLowerCase();
    const hasAuthUser = inputParticipants.some((p) => {
      const email = (p?.email || p?.accountEmail || '').toLowerCase();
      return email && normalizedAuthEmail && email === normalizedAuthEmail;
    });

    const mapped = inputParticipants.map((participant) => ({
      accountEmail: participant?.email || participant?.accountEmail,
      owner: (participant?.email || participant?.accountEmail) === authUser?.email,
    }));

    if (!hasAuthUser && normalizedAuthEmail) {
      mapped.push({
        accountEmail: authUser.email,
        owner: true,
      });
    }

    return mapped;
  }

  get subsection(): string {
    const { url } = this.router;
    const reRoot = /\/projects\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})\//;
    const urlToCheck = url.replace(reRoot, '');

    const reSubsection =
      /([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/;
    const matchResult = urlToCheck.match(reSubsection);

    if (!matchResult || !Array.isArray(matchResult)) {
      return '';
    }

    const sectionId = matchResult[1];
    const subsectionId = matchResult[2];

    const { project } = this.projectService;
    if (!project?.sections) {
      return '';
    }

    const section = project.sections.find((s) => s.id === sectionId);
    const subsection = section?.projectSubSections?.find((ss) => ss.id === subsectionId);

    return subsection?.name || '';
  }

  get section(): string {
    const { url } = this.router;
    const reRoot = /\/projects\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})\//;
    const urlToCheck = url.replace(reRoot, '');

    const reSection = /([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/;
    const matchResult = urlToCheck.match(reSection);

    if (!matchResult || !Array.isArray(matchResult)) {
      return '';
    }

    const sectionId = matchResult[1];

    const { project } = this.projectService;
    if (!project?.sections) {
      return '';
    }

    const section = project.sections.find((s) => s.id === sectionId);

    return section?.name || '';
  }

  registerEventListeners(): void {
    EmitterService.get(AnnotationSendOtherTypesMessage)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data) => {
          if (this.instanceId !== data.instanceId) {
            return;
          }
          if (data.message) {
            this.annotationComment = data.message;
            this.newFiles = data.newFiles;
            this.addNewAnnotation(aiParamsFromAnnotationEvent(data));
          }
        },
      });
  }

  addNewAnnotation(aiParams: AiOptions): void {
    if (this.commentAnnotationPlugin && this.pluginReady && this.annotationComment) {
      if (this.platformCommentHandler) {
        this.platformCommentHandler.saveAnnotation(this.annotationComment, aiParams);
      }
      this.annotationComment = '';
    }
  }

  get isFullscreen(): boolean {
    return this.topMediaInfoService.isFullscreen;
  }

  onSelectThread(event: any) {
    EmitterService.get(AnnotationSelectThreadNew).emit(event);
  }

  onEditMessage(event: any) {
    EmitterService.get(AnnotationEditMessage).emit(event);
  }

  onDeleteMessage(event: any) {
    EmitterService.get(AnnotationDeleteMessage).emit(event);
  }

  public handleUpdateEditMessage(event: any): void {
    const commentData = event?.comment ?? null;
    if (!commentData || !event?.message) return;
    this.commentAnnotationPlugin?.fire?.('updateComment', {
      annotationId: commentData.annotation.id,
      commentId: commentData.comment.id,
      body: event.message,
      files: [],
    });
  }

  public handleSendOtherTypesMessage(event: any): void {
    EmitterService.get(AnnotationSendOtherTypesMessage).emit(event);
  }

  private applyOrDeferNavRequest(request: any): void {
    if (request && request.targetId && request.targetId !== this.pageId) {
      return;
    }
    if (this.pluginReady) {
      this.navigationRequest = request;
      this.platformChatState.consumePendingNavigationRequest();
    } else {
      this.pendingNavRequest = request;
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.isLoadingChange.emit(false);
    this.signatureRequestSub?.unsubscribe();
    if (this.platformCommentHandler && this.pluginReady) {
      this.platformCommentHandler.teardown();
    }
    this.appState.setCommentPluginReady(false);
    if (this.wsId) {
      this.stompService.unsubscribeToService(this.wsId);
    }
  }

  onShowChannelManager(event: any) {
    this.isChannelManagerVisible = event;
  }

  onCloseChannelManager() {
    const activePage = this.appState.getActivePage();
    if (activePage) {
      this.appState.setCommentPageCommentExpanded(activePage, false);
    }
  }

  private fetchSignatureAndInit(): void {
    if (!this.entityType || !this.pageId) {
      this.isLoadingChange.emit(false);
      return;
    }

    this.isLoadingChange.emit(true);
    this.signatureRequestSub?.unsubscribe();
    this.signatureRequestSub = this.rbacSignature
      .requestSignature(this.entityType, this.pageId, this.entityName, this.projectId)
      .pipe(
        catchError(() => of<SignatureRequestResult | null>(null)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        next: (result) => {
          this.signature = result;
          this.panelCoordinator.markInitialized('comment', this.pageId, this.entityType);
          this.initPlugin();
        },
        error: () => {
          this.isLoadingChange.emit(false);
          this.signatureRequestSub = null;
        },
        complete: () => {
          this.signatureRequestSub = null;
        },
      });
  }
}
